import { useCallback, useMemo, useState } from "react";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { Box, Paper } from "@mui/material";

import AddOrderArticleModal from "./AddOrderArticleModal";
import EditOrderArticleModal from "./EditOrderArticleModal";
import CustomGrid from "components/lists/CustomGrid";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "components/lists/EnhancedToolbar";
import useModals from "hooks/useModals";
import { ENTITY } from "constants/entities";
import { formatDateOnlyDMY } from "helpers";
import { buildObservationPreviewSummary } from "helpers/movementArticleObservations";
import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import { Article, Client, OrderDraftArticleRow } from "types";

type Props = {
  rows: OrderDraftArticleRow[];
  articles: Article[];
  client: Client | null;
  disabled?: boolean;
  allowAddRow?: boolean;
  hideFilter?: boolean;
  readOnlyIdentityFields?: boolean;
  onAddRow?: (row: Omit<OrderDraftArticleRow, "localId">) => void;
  onEditRow: (row: OrderDraftArticleRow) => void;
  onDeleteRows: (ids: number[]) => void;
};

export default function OrderArticlesList({
  rows,
  articles,
  client,
  disabled = false,
  allowAddRow = true,
  hideFilter = false,
  readOnlyIdentityFields = false,
  onAddRow,
  onEditRow,
  onDeleteRows,
}: Props) {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [filterValue, setFilterValue] = useState("");
  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();
  const clientFieldConfig = useMemo(
    () => getOrderClientFieldConfig(client),
    [client],
  );

  const selectedRow = useMemo(
    () => rows.find((row) => row.localId === Number(selected[0])) ?? null,
    [rows, selected],
  );

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
  }, [setEditModalOpen]);

  const rowsWithArticleData = useMemo(
    () =>
      rows.map((row) => {
        const article = articles.find(
          (currentArticle) => currentArticle.id === row.article_id,
        );

        return {
          ...row,
          id: row.localId,
          code: article?.code ?? "",
          description: article?.description ?? "",
          observation:
            buildObservationPreviewSummary(row.observation_preview_units ?? []) ??
            "-",
          subtotal_units:
            (Number(row.amount) || 0) * (Number(row.amount_per_container) || 0),
          subtotal_weight:
            (Number(row.amount) || 0) * Number(article?.weight ?? 0),
        };
      }),
    [articles, rows],
  );

  const filteredRows = useMemo(() => {
    const normalizedFilter = filterValue.trim().toLowerCase();

    if (!normalizedFilter) {
      return rowsWithArticleData;
    }

    return rowsWithArticleData.filter((row) =>
      [
        row.code,
        row.description,
        row.location_code,
        row.sub_location_code,
        row.lote,
        row.oc,
        row.fecha_ingreso,
        row.fecha_vencimiento,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedFilter),
        ),
    );
  }, [filterValue, rowsWithArticleData]);

  const gridFields = useMemo(() => {
    const fields: Array<GridColDef & { visible?: boolean }> = [
      {
        field: "code",
        headerName: "Codigo",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "description",
        headerName: "Descripcion",
        headerAlign: "center",
        flex: 1.5,
      },
      {
        field: "amount",
        headerName: "Cantidad",
        headerAlign: "center",
        type: "number",
        flex: 1,
      },
      {
        field: "amount_per_container",
        headerName: "Cantidad por Contenedor",
        headerAlign: "center",
        type: "number",
        flex: 1,
      },
      {
        field: "subtotal_units",
        headerName: "Subtotal",
        headerAlign: "center",
        type: "number",
        flex: 1,
      },
      {
        field: "subtotal_weight",
        headerName: "Peso",
        headerAlign: "center",
        type: "number",
        flex: 1,
        visible: clientFieldConfig.detail.showPeso,
      },
      {
        field: "location_code",
        headerName: "Ubicación",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "sub_location_code",
        headerName: "Sub-ubicación",
        headerAlign: "center",
        flex: 1,
        valueGetter: (params) => params.row.sub_location_code || "-",
      },
      {
        field: "lote",
        headerName: "Lote",
        headerAlign: "center",
        flex: 1,
        visible: clientFieldConfig.detail.showLote,
      },
      {
        field: "oc",
        headerName: "OC",
        headerAlign: "center",
        flex: 1,
        visible: clientFieldConfig.detail.showOc,
      },
      {
        field: "fecha_vencimiento",
        headerName: "Fecha de Vencimiento",
        headerAlign: "center",
        flex: 1,
        visible: clientFieldConfig.detail.showFechaVencimiento,
        valueGetter: (params) =>
          formatDateOnlyDMY(params.row.fecha_vencimiento) || "-",
      },
      {
        field: "observation",
        headerName: "Observaciones",
        headerAlign: "center",
        flex: 1.2,
        renderCell: (params) => params.row.observation,
      },
    ];

    return fields.filter((field) => field.visible !== false);
  }, [clientFieldConfig.detail]);

  const deleteValues = useMemo(
    () =>
      rowsWithArticleData
        .filter((row) => selected.includes(row.id))
        .map((row) => row.description || row.code || `Articulo ${row.id}`),
    [rowsWithArticleData, selected],
  );

  return (
    <>
      {allowAddRow && onAddRow && (
        <AddOrderArticleModal
          open={addModalOpen}
          handleOpen={() => setAddModalOpen((prev) => !prev)}
          client={client}
          existingRows={rows}
          onAdd={onAddRow}
        />
      )}

      {selectedRow && (
        <EditOrderArticleModal
          open={editModalOpen}
          handleOpen={handleCloseEditModal}
          value={selectedRow}
          articles={articles}
          client={client}
          readOnlyIdentityFields={readOnlyIdentityFields}
          onEdit={onEditRow}
        />
      )}

      <DeleteConfirmationModaL
        open={deleteModalOpen}
        handleOpen={() => setDeleteModalOpen((prev) => !prev)}
        entityName={ENTITY.ARTICLES}
        values={deleteValues}
        handleDelete={() => {
          onDeleteRows(selected.map((id) => Number(id)));
          setSelected([]);
        }}
      />

      <Box width="100%">
        <Paper sx={{ width: "100%", mb: 2 }}>
          <EnhancedTableToolbar
            numSelected={selected.length}
            disabled={disabled}
            handleAddModalOpen={() => {
              if (!allowAddRow) {
                return;
              }

              setAddModalOpen((prev) => !prev);
            }}
            handleEditModalOpen={() => {
              setEditModalOpen((prev) => !prev);
            }}
            handleDeleteModalOpen={() => setDeleteModalOpen((prev) => !prev)}
            onFilterChange={hideFilter ? undefined : setFilterValue}
            hideAddButton={!allowAddRow}
          />

          <CustomGrid<any>
            structure={{
              name: "order_articles",
              labelName: "Articulos del pedido",
              fields: gridFields,
            }}
            rows={filteredRows}
            selectionModel={selected}
            onRowSelection={setSelected}
            checkboxSelection={!disabled}
            getRowId={(row) => row.id}
          />
        </Paper>
      </Box>
    </>
  );
}
